/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface PassdownLogAcknowledgementProps {
  id: string;
  passdownLogId: string;
  dateAcknowledged: Date;
  acknowledgedById: string;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class PassdownLogAcknowledgementRecord extends Record<PassdownLogAcknowledgementProps>({
  id: null as any,
  passdownLogId: null as any,
  dateAcknowledged: null as any,
  acknowledgedById: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    passdownLogId: string;
    dateAcknowledged: string | number | Date;
    acknowledgedById: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): PassdownLogAcknowledgementRecord {
    if (json == null) {
      throw new Error('PassdownLogAcknowledgementRecord.fromJS: parameter cannot be null');
    }
    return new PassdownLogAcknowledgementRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogAcknowledgementJson.id',
      ),
      passdownLogId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'passdownLogId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogAcknowledgementJson.passdownLogId',
      ),
      dateAcknowledged: new Date(
        getFromOrThrow(
          json,
          (j) => propValueOrDefault(j, 'dateAcknowledged', '0001-01-01T00:00:00.00Z'),
          'PassdownLogAcknowledgementJson.dateAcknowledged',
        ),
      ),
      acknowledgedById: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'acknowledgedById', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogAcknowledgementJson.acknowledgedById',
      ),
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'PassdownLogAcknowledgementJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    passdownLogId: string;
    dateAcknowledged: string | number | Date;
    acknowledgedById: string;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [PassdownLogAcknowledgementRecord, Error[]] {
    if (json == null) {
      throw new Error('PassdownLogAcknowledgementRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new PassdownLogAcknowledgementRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogAcknowledgementJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      passdownLogId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'passdownLogId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogAcknowledgementJson.passdownLogId',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      dateAcknowledged: tryOrDefault(
        () =>
          new Date(
            getFromOrThrow(
              json,
              (j) => propValueOrDefault(j, 'dateAcknowledged', '0001-01-01T00:00:00.00Z'),
              'PassdownLogAcknowledgementJson.dateAcknowledged',
            ),
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      acknowledgedById: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) =>
              propValueOrDefault(j, 'acknowledgedById', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogAcknowledgementJson.acknowledgedById',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'PassdownLogAcknowledgementJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: PassdownLogAcknowledgementProps) {
    super(props);
    if (props == null) {
      throw new Error('PassdownLogAcknowledgementRecord constructor parameter cannot be null');
    }
  }
}

export abstract class PassdownLogAcknowledgementViewModelBase
  implements PassdownLogAcknowledgementProps
{
  protected readonly _props: PassdownLogAcknowledgementProps;
  private _json?: PassdownLogAcknowledgementProps;

  get id(): string {
    return this._props.id;
  }
  get passdownLogId(): string {
    return this._props.passdownLogId;
  }
  get dateAcknowledged(): Date {
    return this._props.dateAcknowledged;
  }
  get acknowledgedById(): string {
    return this._props.acknowledgedById;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  constructor(props: PassdownLogAcknowledgementProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        passdownLogId: this._props.passdownLogId,
        dateAcknowledged: this._props.dateAcknowledged,
        acknowledgedById: this._props.acknowledgedById,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllPassdownLogAcknowledgementEntities = (state: AppState) =>
  state.entityStores.passdownLogAcknowledgement.entities;

export const selectPassdownLogAcknowledgementById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLogAcknowledgement.entities.get(id);

export const selectPassdownLogAcknowledgementMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.passdownLogAcknowledgement.meta.get(id);

export const selectPassdownLogAcknowledgementAndMetadataById = (id: string) =>
  createSelector(
    selectPassdownLogAcknowledgementById(id),
    selectPassdownLogAcknowledgementMetadataById(id),
    (passdownLogAcknowledgement, meta) => ({ passdownLogAcknowledgement, meta }),
  );

export function selectPassdownLogAcknowledgementEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllPassdownLogAcknowledgementEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, PassdownLogAcknowledgementRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectPassdownLogAcknowledgementEntitiesByPassdownLogId(passdownLogId: string) {
  return createSelector(selectAllPassdownLogAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.passdownLogId === passdownLogId).valueSeq(),
  );
}

export function selectPassdownLogAcknowledgementEntitiesByDateAcknowledged(dateAcknowledged: Date) {
  return createSelector(selectAllPassdownLogAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.dateAcknowledged === dateAcknowledged).valueSeq(),
  );
}

export function selectPassdownLogAcknowledgementEntitiesByAcknowledgedById(
  acknowledgedById: string,
) {
  return createSelector(selectAllPassdownLogAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.acknowledgedById === acknowledgedById).valueSeq(),
  );
}

export function selectPassdownLogAcknowledgementEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllPassdownLogAcknowledgementEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
